import { TRPCClientError } from '@trpc/client';
import { useLayoutEffect, useRef, useState } from 'react';
import { useAutoSave } from '../../hooks/useAutoSave.ts';
import { useSaveFlow } from '../../hooks/useSaveFlow.ts';
import { ProtocolFinal, RecordDraft } from '../../types/sharedTypeImpl.ts';
import { trpc } from '../../utils/trpc/trpc.ts';
import { finalizeRecord } from './finalizeRecord.ts';


interface Props {
    record: RecordDraft | null;
    protocol: ProtocolFinal | undefined;
    isViewMode: boolean;
    isFinishedRecord: boolean;
    isDirty: boolean;
    setIsDirty: (isDirty: boolean) => void;
    isFinalSavedRef: React.MutableRefObject<boolean>;
    onFinalSaved: () => void;
}

export function useRecordSave({
    record,
    protocol,
    isViewMode,
    isFinishedRecord,
    isDirty,
    setIsDirty,
    isFinalSavedRef,
    onFinalSaved,
}: Props
) {
    const [draftSaveError, setDraftSaveError] = useState<string | null>(null);
    const [saveFailAlerted, setSaveFailAlerted] = useState(false);
    const latestRecordRef = useRef(record);

    const saves = useSaveFlow();
    useLayoutEffect(() => {
        latestRecordRef.current = record;
    }, [record]);

    const trpcUtils = trpc.useUtils();
    const upsertRecord = trpc.records.upsert.useMutation({
        onSuccess: () => trpcUtils.records.invalidate(),
    });

    useAutoSave({
        isViewMode,
        isFinalized: isFinishedRecord,
        isDirty,
        isFinalSaving: saves.isFinalSaving(),
        autoSaveCallback: async () => await handleDraftSave()
    });

    const handleDraftSave = () => {
        if (!record || isFinalSavedRef.current) return;

        const save = saves.draftSave(async () => {
            try {
                setDraftSaveError(null);
                console.log('Draft save started');

                await upsertRecord.mutateAsync(record);

                if (latestRecordRef.current === record) setIsDirty(false);// Only if there were no edits during the save
                setSaveFailAlerted(false);
                console.log('Draft save success');
                return true;
            } catch (e) {
                handleDraftSaveError(e);
                return false;
            }
        });
        return save;
    }

    const handleFinalSave = () => {
        if (!record || !protocol || isFinalSavedRef.current) return;

        const recordFinal = finalizeRecord(record, protocol);
        const save = saves.finalSave(async () => {
            try {
                console.log('Final save started');
                await upsertRecord.mutateAsync(recordFinal);

                setIsDirty(false);
                isFinalSavedRef.current = true;
                console.log('Final save successful');
                onFinalSaved();
            } catch (e) {
                alert(`Failed to save record.`);
            }
        });
        return save;
    }

    const handleDraftSaveError = (error: any) => {
        let shortMessage;
        let longMessage;
        if (error instanceof TRPCClientError && error.data?.code === 'CONFLICT') {
            shortMessage = 'Already finalized';
            longMessage = 'This record has already been finalized. It may have been finalized in another tab or by another user.';
        } else {
            shortMessage = longMessage = saves.isFinalSaving()
                ? 'Save failed.'
                : 'Draft save failed';
        }

        setDraftSaveError(shortMessage);
        if (!saveFailAlerted) {
            alert(longMessage);
            setSaveFailAlerted(true);
        }
    }


    return {
        isSaving: saves.isSaving,
        draftSaveError,
        handleDraftSave,
        handleFinalSave,
    }
}
